import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface MessageFeedbackProps {
  messageContent: string;
}

const FEEDBACK_CATEGORIES = [
  { value: "not_helpful", label: "Wasn't helpful" },
  { value: "wrong_info", label: "Got something wrong" },
  { value: "didnt_understand", label: "Didn't understand what I asked" },
  { value: "too_long", label: "Too long or too much" },
  { value: "too_short", label: "Not enough detail" },
  { value: "wrong_tone", label: "Didn't sound like Kin" },
  { value: "other", label: "Something else" },
];

const MessageFeedback = ({ messageContent }: MessageFeedbackProps) => {
  const { user } = useAuth();
  const [rating, setRating] = useState<"up" | "down" | null>(null);
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherText, setOtherText] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when tapping outside
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setShowOtherInput(false);
        setOtherText("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  const writeFeedback = async (
    value: "up" | "down",
    category?: string,
    suggestionText?: string
  ) => {
    // Fire-and-forget web3forms (existing behavior, preserved)
    fetch("https://api.web3forms.com/submit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        access_key: "eabca370-87d5-4393-aab8-2f6e3778c2dc",
        subject: "Kin Response Feedback",
        message: messageContent.slice(0, 100),
        rating: value,
        category: category || null,
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => {});

    // Supabase write — single row per response, swap on flip.
    try {
      if (!user) return;

      const feedback_type = value === "up" ? "thumbs_up" : "thumbs_down";

      if (feedbackId) {
        await supabase
          .from("response_feedback")
          .update({
            feedback_type,
            feedback_category: category || null,
            suggestion_text: suggestionText || null,
          })
          .eq("id", feedbackId);
      } else {
        const { data, error } = await supabase
          .from("response_feedback")
          .insert({
            user_id: user.id,
            message_content: messageContent.slice(0, 200),
            feedback_type,
            feedback_category: category || null,
            suggestion_text: suggestionText || null,
          })
          .select("id")
          .single();
        if (!error && data) setFeedbackId(data.id);
      }
    } catch {
      /* silent */
    }
  };

  const handleRate = (value: "up" | "down") => {
    if (rating === value) {
      // Re-tap same rating — if down is showing dropdown, close it
      if (value === "down" && showDropdown) {
        setShowDropdown(false);
        setShowOtherInput(false);
        setOtherText("");
      }
      return;
    }

    if (value === "up") {
      // Thumbs up — immediate, same as before
      setRating("up");
      setShowDropdown(false);
      setShowOtherInput(false);
      setOtherText("");
      toast("Thanks for the feedback 💜", { duration: 2000 });
      writeFeedback("up");
    } else {
      // Thumbs down — show dropdown, don't write yet
      setShowDropdown(true);
      setShowOtherInput(false);
      setOtherText("");
    }
  };

  const handleCategorySelect = (category: string) => {
    if (category === "other") {
      setShowOtherInput(true);
      return;
    }
    setRating("down");
    setShowDropdown(false);
    toast("Got it. I'll keep improving 💜", { duration: 2000 });
    writeFeedback("down", category);
  };

  const handleOtherSubmit = () => {
    setRating("down");
    setShowDropdown(false);
    setShowOtherInput(false);
    toast("Got it. I'll keep improving 💜", { duration: 2000 });
    writeFeedback("down", "other", otherText || undefined);
    setOtherText("");
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => handleRate("up")}
          className="text-sm transition-colors"
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: "14px",
            color: rating === "up" ? "#5050C8" : "#54525C",
            opacity: rating && rating !== "up" ? 0.4 : 1,
          }}
          aria-label="Thumbs up"
        >
          👍
        </button>
        <button
          onClick={() => handleRate("down")}
          className="text-sm transition-colors"
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: "14px",
            color: rating === "down" || showDropdown ? "#5050C8" : "#54525C",
            opacity: rating && rating !== "down" && !showDropdown ? 0.4 : 1,
          }}
          aria-label="Thumbs down"
        >
          👎
        </button>
      </div>

      {showDropdown && (
        <div
          style={{
            marginTop: "8px",
            width: "260px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #E0E0F0",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            zIndex: 50,
            overflow: "hidden",
            animation: "feedbackDropdownIn 150ms ease-out",
          }}
        >
          <div
            style={{
              padding: "12px 16px 8px",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              color: "#54525C",
            }}
          >
            What went wrong?
          </div>

          {FEEDBACK_CATEGORIES.map((cat, i) => (
            <button
              key={cat.value}
              onClick={() => handleCategorySelect(cat.value)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "12px 16px",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: "14px",
                color: "#1C1A3E",
                backgroundColor: "transparent",
                border: "none",
                borderTop: i === 0 ? "1px solid #F0F0F5" : "none",
                borderBottom:
                  i < FEEDBACK_CATEGORIES.length - 1
                    ? "1px solid #F0F0F5"
                    : "none",
                cursor: "pointer",
                transition: "background-color 100ms",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#F0F0FF")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              {cat.label}
            </button>
          ))}

          {showOtherInput && (
            <div
              style={{
                padding: "8px 16px 12px",
                borderTop: "1px solid #F0F0F5",
              }}
            >
              <textarea
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="Tell us more (optional)"
                rows={2}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: "13px",
                  color: "#1C1A3E",
                  backgroundColor: "#F0F0FF",
                  border: "1px solid #E0E0F0",
                  borderRadius: "8px",
                  resize: "none",
                  outline: "none",
                }}
              />
              <button
                onClick={handleOtherSubmit}
                style={{
                  marginTop: "8px",
                  padding: "8px 20px",
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#FFFFFF",
                  backgroundColor: "#5050C8",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                Submit
              </button>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes feedbackDropdownIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default MessageFeedback;